export type PlanId = 'starter' | 'growth' | 'pro';

export interface PlanLimitsConfig {
  max_users: number | null;
  max_products: number | null;
  max_sales_orders_per_month: number | null;
}

// null = unlimited
export const PLAN_LIMITS: Record<PlanId, PlanLimitsConfig> = {
  starter: { max_users: 2, max_products: 8, max_sales_orders_per_month: 25 },
  growth: { max_users: null, max_products: 50, max_sales_orders_per_month: null },
  pro: { max_users: null, max_products: null, max_sales_orders_per_month: null },
};

// Used by webhook and tenant creation to set max_users on tenants table
export const PLAN_MAX_USERS: Record<PlanId, number> = {
  starter: 2,
  growth: 999,
  pro: 999,
};

// Feature-to-plan gating: which plan tier is required to access a feature
export const PLAN_FEATURE_REQUIREMENTS: Record<string, PlanId> = {
  'transport-orders': 'growth',
  'receipt-scan': 'growth',
  // DECISION 70 — recurring orders and the buyer-facing portal are ONE feature
  // for pricing. The argument is structural rather than a taste call: recurring
  // orders MINT sales orders unattended (`portal_scheduler_collect` drafts from
  // every active recurring order, and promotion turns those into SOs), while
  // Starter meters sales orders at 25/month. A tier that meters SOs cannot host
  // a feature that generates them with nobody touching it.
  //
  // ⚠️ This gate reads `subscription_plan` ONLY. A cancelled tenant still reads
  // 'growth' (`customer.subscription.deleted` leaves the plan untouched), so it
  // is `ProtectedRoute`/`SubscriptionGate` that stops them, one layer up. The
  // buyer-facing portal has neither check — gating that side is S7, and it needs
  // a server-side seam because nothing in the buyer's path reads the operator's
  // plan.
  'customer-portal': 'growth',
};

const PLAN_TIER_ORDER: Record<PlanId, number> = {
  starter: 0,
  growth: 1,
  pro: 2,
};

export function canAccessPlanFeature(currentPlan: PlanId, feature: string): boolean {
  const requiredPlan = PLAN_FEATURE_REQUIREMENTS[feature];
  if (!requiredPlan) return true;
  return PLAN_TIER_ORDER[currentPlan] >= PLAN_TIER_ORDER[requiredPlan];
}

export function getRequiredPlanName(feature: string): string {
  const requiredPlan = PLAN_FEATURE_REQUIREMENTS[feature];
  if (!requiredPlan) return '';
  return requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1);
}

/** What the portal side of a tenant looks like today — the two things a downgrade below Growth closes. */
export interface PortalUsage {
  /** Distinct buyers holding at least one location grant. */
  portalBuyers: number;
  /** recurring_orders with status = 'active'. */
  activeRecurringOrders: number;
}

/**
 * What moving to `targetPlan` would CLOSE, in the operator's own numbers — or null when nothing would.
 *
 * DECISION 70: recurring orders and the buyer-facing portal are one Growth+ feature, and S7 made
 * that true on the server: below Growth, every buyer RPC refuses with PORTAL_CLOSED and the scheduler
 * mints nothing. This is S7b-i, the operator-side softening — it reads what is about to stop and says
 * so on the plan card, with counts. It is a WARNING, not a block: the downgrade stays allowed, and D70
 * records it cannot be the only defence because Stripe can downgrade by webhook, bypassing this page.
 *
 * Null (not an empty string) when the target still carries the portal, or when there is nothing to
 * close — so a caller can render nothing rather than an empty box.
 */
export function portalDowngradeImpact(targetPlan: PlanId, usage: PortalUsage): string | null {
  if (canAccessPlanFeature(targetPlan, 'customer-portal')) return null;
  const buyers = Math.max(0, Math.trunc(usage.portalBuyers));
  const recurring = Math.max(0, Math.trunc(usage.activeRecurringOrders));
  if (buyers === 0 && recurring === 0) return null;

  const planName = targetPlan.charAt(0).toUpperCase() + targetPlan.slice(1);
  const parts: string[] = [];
  if (buyers > 0) parts.push(`closes the customer portal for ${buyers} ${buyers === 1 ? 'buyer' : 'buyers'}`);
  if (recurring > 0) parts.push(`stops ${recurring} ${recurring === 1 ? 'recurring order' : 'recurring orders'}`);
  // History is the one buyer RPC exempted from the entitlement gate (S7), so this promise holds.
  const tail = buyers > 0 ? ' Buyers keep their order history.' : '';
  return `Moving to ${planName} ${parts.join(' and ')}.${tail}`;
}

// Check if current usage fits within a target plan's limits
// Does NOT check sales orders (resets monthly, not a persistent overage)
export function canFitInPlan(
  planId: PlanId,
  usage: { products: number; users: number }
): { fits: boolean; issues: string[] } {
  const planLimits = PLAN_LIMITS[planId];
  const planName = planId.charAt(0).toUpperCase() + planId.slice(1);
  const issues: string[] = [];

  if (planLimits.max_products !== null && usage.products > planLimits.max_products) {
    issues.push(`You have ${usage.products} products \u2014 ${planName} allows ${planLimits.max_products}`);
  }

  if (planLimits.max_users !== null && usage.users > planLimits.max_users) {
    issues.push(`You have ${usage.users} team members \u2014 ${planName} allows ${planLimits.max_users}`);
  }

  return { fits: issues.length === 0, issues };
}
